import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query'

import {
    getInitialValuesCompany,
    getInitialValuesPersonal,
} from 'features/Clients/model/getInitialValues'
import { IPropsFile } from 'features/Clients/types'
import { handleEditProfile } from 'features/Clients/api/handleEditProfile'
import { getChangedValues } from 'shared/utils/getChangedValues'
import { useFileUploadUp } from 'shared/hooks/useFileUploadUp'
import { Counterparty } from 'entities/counterparties/model'
import { useParams } from 'react-router-dom'
import { DynamicCreationReducerHandler } from 'pages/clientsCreate/model/dynamicCreationReducers'
import { ContactDTO } from 'entities/counterparties/model/dtoTypes'

export enum FormKeys {
    Personal = 'personalForm',
    Company = 'companyForm',
}

interface UseFormConfigProps {
    isCompany: boolean
    counterparty?: Counterparty
    refetch: (
        options?: RefetchOptions | undefined,
    ) => Promise<QueryObserverResult<unknown, Error>>
    setIsEditable: (e: boolean) => void
    setEditButtonText: (e: string) => void
    dynamicCreationHandler: DynamicCreationReducerHandler | null
    defaultContacts?: ContactDTO[]
}

export const useFormConfig = (props: UseFormConfigProps) => {
    const {
        isCompany,
        counterparty,
        refetch,
        setIsEditable,
        setEditButtonText,
        dynamicCreationHandler,
        defaultContacts
    } = props

    const { id } = useParams()
    const dispatch = useDispatch()

    const [editing, setEditing] = useState<boolean>(false)
    const [photo, setPhoto] = useState<File | null>(null)
    const [newFiles, setNewFiles] = useState<IPropsFile[]>([])
    const [removedFiles, setRemovedFiles] = useState<IPropsFile[]>([])

    const { handleFileUpload } = useFileUploadUp()

    const onPersonalSubmit = async (values: any) => {
        setEditing(true)
        const { uploadedPhotoInfo, uploadedFilesInfo } = await handleFileUpload(
            photo,
            newFiles?.map(file => file.file!) || [],
        )
        const initialValues = getInitialValuesPersonal(counterparty!)
        const updatedValues = getChangedValues(initialValues, values)
        delete updatedValues.contacts

        if (!Object.keys(updatedValues).length) {
            setEditing(false)
            return
        }

        if (uploadedPhotoInfo) {
            updatedValues.profile_photo_hash_name = uploadedPhotoInfo.hash_name
        }

        if (removedFiles.length > 0) {
            updatedValues.removed_document_photos_hash_names = removedFiles
                .map(file => file?.hash_name)
                .filter(
                    hash_name => hash_name !== null && hash_name !== undefined,
                )
            setRemovedFiles([])
        }

        if (newFiles.length > 0) {
            updatedValues.new_document_photos_hash_names =
                uploadedFilesInfo.map((file, index) => ({
                    hash_name: file.hash_name,
                    file_name: newFiles[index].name,
                }))
            setNewFiles([])
        }

        handleEditProfile(
            updatedValues as any,
            refetch,
            setIsEditable,
            setEditButtonText,
            dispatch,
            id,
        ).then(() => {
            setTimeout(() => setEditing(false), 1000)
        })
    }

    const onCompanySubmit = async (values: any) => {
        setEditing(true)
        if (dynamicCreationHandler) {
            dynamicCreationHandler.beforeCreateAction()
        }

        const { uploadedPhotoInfo } = await handleFileUpload(photo, [])
        const initialValues = getInitialValuesCompany(counterparty!, defaultContacts)
        const updatedValues = getChangedValues(initialValues, values)

        if (!Object.keys(updatedValues).length) {
            setEditing(false)

            if (dynamicCreationHandler) {
                dynamicCreationHandler.postCreateAction()
            }
            return
        }
        delete updatedValues.contacts

        if (uploadedPhotoInfo) {
            updatedValues.profile_photo_hash_name = uploadedPhotoInfo.hash_name
        }

        if (removedFiles.length > 0) {
            updatedValues.removed_document_photos_hash_names = removedFiles.map(
                file => ({
                    hash_name: file.hash_name,
                }),
            )
        }

        if (newFiles.length > 0) {
            updatedValues.new_document_photos_hash_names = newFiles.map(
                file => ({
                    hash_name: file.hash_name,
                }),
            )
        }

        handleEditProfile(
            updatedValues as any,
            refetch,
            setIsEditable,
            setEditButtonText,
            dispatch,
            id,
        ).then(res => {
            setTimeout(() => setEditing(false), 1000)

            if (dynamicCreationHandler) {
                dynamicCreationHandler.postCreateAction(res)
            }
        })
    }

    return {
        editing,
        setPhoto,
        setNewFiles,
        setRemovedFiles,

        key: isCompany ? FormKeys.Company : FormKeys.Personal,
        onCompanySubmit,
        onPersonalSubmit,
    }
}
